import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ConversationsPage.css';
import { useTheme } from './useTheme';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003';

// Cargar Font Awesome
const loadFontAwesome = () => {
    if (!document.getElementById('font-awesome-css')) {
        const link = document.createElement('link');
        link.id = 'font-awesome-css';
        link.rel = 'stylesheet';
        link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
        document.head.appendChild(link);
    }
};

function ConversationsPage() {
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();
    const [conversaciones, setConversaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showNewChatModal, setShowNewChatModal] = useState(false);
    const [newNumber, setNewNumber] = useState('');

    useEffect(() => {
        loadFontAwesome();
        cargarConversaciones();
    }, []);

    const cargarConversaciones = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/conversaciones`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al cargar conversaciones');
            }

            const data = await response.json();
            setConversaciones(data);
            setError(null);
        } catch (err) {
            console.error('Error:', err);
            setError('No se pudieron cargar las conversaciones');
        } finally {
            setLoading(false);
        }
    };

    const formatPhoneNumber = (numero) => {
        if (!numero) return 'Sin número';
        const cleanNumber = numero.replace('@c.us', '');
        if (cleanNumber.length >= 10) {
            const codigo = cleanNumber.slice(0, 2);
            const uno = cleanNumber.slice(2, 3);
            const area = cleanNumber.slice(3, 5);
            const num1 = cleanNumber.slice(5, 9);
            const num2 = cleanNumber.slice(9);
            return `+${codigo} ${uno} ${area} ${num1} ${num2}`;
        }
        return cleanNumber;
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diff = now - date;

        if (diff < 86400000) {
            return date.toLocaleTimeString('es-MX', {
                hour: '2-digit',
                minute: '2-digit',
            });
        }

        if (diff < 172800000) {
            return 'Ayer';
        }

        if (diff < 604800000) {
            return date.toLocaleDateString('es-MX', { weekday: 'short' });
        }

        return date.toLocaleDateString('es-MX', {
            day: '2-digit',
            month: '2-digit',
        });
    };

    const truncateMessage = (mensaje, maxLength = 50) => {
        if (!mensaje) return '';
        if (mensaje.startsWith('/media/')) {
            if (mensaje.match(/\.(jpg|jpeg|png|gif|webp)/i)) return '📷 Imagen';
            if (mensaje.match(/\.(mp3|ogg|opus)/i)) return '🎵 Audio';
            if (mensaje.match(/\.(mp4|avi|mov)/i)) return '🎥 Video';
            return '📎 Archivo';
        }
        return mensaje.length > maxLength
            ? mensaje.substring(0, maxLength) + '...'
            : mensaje;
    };

    const handleOpenChat = (contacto) => {
        navigate(`/chat/${encodeURIComponent(contacto)}`);
    };

    const handleLogout = () => {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('token');
        navigate('/');
    };

    const handleNewChat = () => {
        if (!newNumber.trim()) {
            alert('Por favor ingresa un número de teléfono');
            return;
        }

        // Formatear el número para WhatsApp (agregar @c.us si no lo tiene)
        let formattedNumber = newNumber.replace(/\D/g, ''); // Solo números

        // Si no tiene código de país, asumir México (+52)
        if (formattedNumber.length === 10) {
            formattedNumber = '521' + formattedNumber;
        } else if (!formattedNumber.startsWith('52')) {
            formattedNumber = '52' + formattedNumber;
        }

        const chatId = formattedNumber + '@c.us';
        setShowNewChatModal(false);
        setNewNumber('');
        navigate(`/chat/${encodeURIComponent(chatId)}`);
    };

    if (loading) {
        return (
            <div className="conversations-container">
                <div className="loading">Cargando conversaciones...</div>
            </div>
        );
    }

    return (
        <div className="conversations-container">
            <div className="conversations-header">
                <div className="header-actions">
                    <button onClick={() => setShowNewChatModal(true)} className="new-chat-btn" title="Nuevo Chat">
                        <i className="fas fa-plus"></i>
                        <span className="btn-text">Nuevo Chat</span>
                    </button>
                    <button onClick={cargarConversaciones} className="refresh-btn" title="Actualizar">
                        <i className="fas fa-sync-alt"></i>
                        <span className="btn-text">Actualizar</span>
                    </button>
                    <button onClick={toggleTheme} className="theme-btn" title={theme === 'light' ? 'Modo Oscuro' : 'Modo Claro'}>
                        <i className={theme === 'light' ? 'fas fa-moon' : 'fas fa-sun'}></i>
                        <span className="btn-text">{theme === 'light' ? 'Oscuro' : 'Claro'}</span>
                    </button>
                    <button onClick={handleLogout} className="logout-btn" title="Salir">
                        <i className="fas fa-sign-out-alt"></i>
                        <span className="btn-text">Salir</span>
                    </button>
                </div>
            </div>

            {error && (
                <div className="error-message">{error}</div>
            )}

            {conversaciones.length === 0 ? (
                <div className="empty-state">
                    <p>No hay conversaciones</p>
                    <small>Los mensajes aparecerán aquí cuando recibas o envíes alguno</small>
                </div>
            ) : (
                <div className="conversations-list">
                    {conversaciones.map((conv) => (
                        <div
                            key={conv.contacto}
                            className="conversation-item"
                            onClick={() => handleOpenChat(conv.contacto)}
                        >
                            <div className="conversation-avatar">
                                {formatPhoneNumber(conv.contacto).charAt(1)}
                            </div>
                            <div className="conversation-content">
                                <div className="conversation-header">
                                    <span className="contact-name">
                                        {formatPhoneNumber(conv.contacto)}
                                    </span>
                                    <span className="timestamp">
                                        {formatTimestamp(conv.ultimo_timestamp)}
                                    </span>
                                </div>
                                <div className="last-message">
                                    {conv.ultimo_remitente === 'yo' && <span className="you-prefix">Tú: </span>}
                                    {truncateMessage(conv.ultimo_mensaje)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal para nuevo chat */}
            {showNewChatModal && (
                <div className="modal-overlay" onClick={() => setShowNewChatModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Nuevo Chat</h2>
                        <p className="modal-description">
                            Ingresa el número de teléfono (10 dígitos para México)
                        </p>
                        <input
                            type="tel"
                            className="modal-input"
                            placeholder="5629037511"
                            value={newNumber}
                            onChange={(e) => setNewNumber(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleNewChat()}
                            autoFocus
                        />
                        <div className="modal-actions">
                            <button onClick={() => setShowNewChatModal(false)} className="modal-btn-cancel">
                                Cancelar
                            </button>
                            <button onClick={handleNewChat} className="modal-btn-confirm">
                                Iniciar Chat
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ConversationsPage;
